<script lang="ts">
  export type FieldValidator = (args: { value: string }) => string | undefined
  export type FieldAsyncValidator = (args: {
    value: string
    signal: AbortSignal
  }) => Promise<string | undefined>
  export interface FieldValidators {
    onChange?: FieldValidator
    onChangeAsync?: FieldAsyncValidator
    onChangeAsyncDebounceMs?: number
    onBlur?: FieldValidator
    onBlurAsync?: FieldAsyncValidator
    onBlurAsyncDebounceMs?: number
    onSubmit?: FieldValidator
    onSubmitAsync?: FieldAsyncValidator
  }
</script>

<script setup lang="ts" generic="TForm extends { Field: Component }">
  import { computed, inject, type Component, type HTMLAttributes } from 'vue'
  import { IconLoading } from '@gat-ship/icons'
  import { InputGroup, InputGroupAddon } from '../ui/input-group'
  import { FieldError, FIELD_CONTEXT_KEY } from '../ui/field'
  import { Label } from '../ui/label'
  import { TooltipHint } from '../tooltips'
  import AiFieldHint from './AiFieldHint.vue'
  import FormFieldAsyncMessages from './FormFieldAsyncMessages.vue'
  import { useClearAiHint, useAiLoading } from './useAiFormFill'
  import type {
    FormFieldMessage,
    FormFieldMessagesAsyncHandler
  } from './form-field-messages-types'

  const props = withDefaults(
    defineProps<{
      form: TForm
      name: string
      label?: string
      description?: string
      class?: HTMLAttributes['class']
      required?: boolean | string
      validators?: FieldValidators
      /**
       * Imperative messages (warnings / info / error) rendered below the
       * control. `variant: 'error'` entries are promoted into the tanstack
       * error map so they block submit. Composed with `messagesAsync`.
       */
      messages?: FormFieldMessage[]
      /**
       * Async handler producing messages from the current field value
       * (debounced, cancellable) — e.g. duplicate / uniqueness checks.
       */
      messagesAsync?: FormFieldMessagesAsyncHandler
      /** Debounce (ms) before `messagesAsync` fires. Default 400. */
      messagesAsyncDebounceMs?: number
      /**
       * When `false`, the default slot is rendered directly without the
       * surrounding `<InputGroup>` border, while the label / error / variant
       * chrome is preserved. Use for non-text controls that bring their own
       * border (select, combobox, phone, date-picker, checkbox, switch,
       * radio) so they don't get a double border in the `default` variant.
       * Defaults to `true` (text-shaped inputs composed with `InputGroup*`).
       */
      inputGroup?: boolean
      /**
       * Opt into AI-form-fill chrome: a per-field sparkle provenance
       * indicator next to the label. The slot also receives `aiLoading` and a
       * hint-clearing `handleChange` wrapper — bind those on the control to
       * get the loading state and clear-hint-on-edit behaviours. No-op unless
       * an ancestor called `useAiFormFill`.
       */
      ai?: boolean
      /**
       * Tooltip help text rendered as a `TooltipHint` (question-mark icon)
       * immediately to the right of the label. Only shown alongside the
       * default label rendering — overriding the `#label` slot owns its own
       * chrome.
       */
      hint?: string
    }>(),
    {
      // Boolean props without a default are cast to `false` when absent
      // (Vue boolean casting), so this default is REQUIRED — otherwise an
      // omitted `input-group` reads as `false` and the stacked InputGroup
      // row is skipped, dropping every text field to the default variant.
      inputGroup: true
    }
  )

  const context = inject(FIELD_CONTEXT_KEY, { variant: 'default' as const })

  const wrapInInputGroup = computed(() => props.inputGroup !== false)

  const hasMessagesFeature = computed(
    () => !!props.messages || !!props.messagesAsync
  )

  // AI provenance wiring. Both are no-ops (false / no-op fn) when no
  // `useAiFormFill` context is provided, so they're safe to call always.
  const clearAiHint = useClearAiHint()
  const aiLoading = useAiLoading()

  function isInvalid(field: {
    state: { meta: { isTouched: boolean; errors: unknown[] } }
  }) {
    return field.state.meta.isTouched && field.state.meta.errors.length > 0
  }

  /**
   * Hint-clearing change wrapper exposed to the slot. Consumers that opt
   * into AI fill bind `@update:model-value="handleChange"` (instead of
   * `field.handleChange`) so the sparkle disappears once the user edits.
   */
  function makeHandleChange(field: { handleChange: (v: unknown) => void }) {
    return (value: unknown) => {
      field.handleChange(value)
      clearAiHint(props.name)
    }
  }
</script>

<template>
  <component :is="form.Field" :name="name" :validators="validators">
    <template #default="{ field }">
      <slot
        name="root"
        :field="field"
        :invalid="isInvalid(field)"
        :ai-loading="aiLoading"
        :handle-change="makeHandleChange(field)"
      >
        <!--
          Stacked row: everything inside one InputGroup row (no border,
          the parent FieldGroup card owns the chrome). Used for fields
          that live in a FieldGroup card — text-shaped controls AND
          non-text controls placed alongside them (FieldGroup's reset
          classes strip the inner control border so they read flush).

          `input-group="false"` opts OUT of the row entirely and falls
          through to the plain label/control/error layout below — for
          standalone controls (radio cards, lone checkbox/switch) that
          are NOT inside a FieldGroup card.
        -->
        <InputGroup
          v-if="context.variant === 'stacked' && wrapInInputGroup"
          :class="$props.class"
        >
          <InputGroupAddon align="block-start">
            <slot name="label" :field="field">
              <Label
                v-if="label"
                :for="name"
                class="font-medium leading-none text-neutral-700 dark:text-neutral-200"
                :class="{
                  'inline-flex items-center gap-1.5':
                    ai || hint || field.state.meta.isValidating,
                  'text-primary': !!field.state.value && !isInvalid(field),
                  'text-destructive': isInvalid(field)
                }"
              >
                {{ label }}
                <AiFieldHint v-if="ai" :name="name" />
                <TooltipHint v-if="hint" :text="hint" />
                <IconLoading
                  v-if="field.state.meta.isValidating"
                  class="text-muted-foreground h-3.5 w-3.5 animate-spin"
                  title="Checking…"
                />
              </Label>
            </slot>
            <slot name="label-end" :field="field">
              <span
                v-if="!required && label"
                class="text-muted-foreground/60 ml-auto text-xs font-normal"
              >
                optional
              </span>
            </slot>
          </InputGroupAddon>

          <slot
            :field="field"
            :invalid="isInvalid(field)"
            :ai-loading="aiLoading"
            :handle-change="makeHandleChange(field)"
          />

          <InputGroupAddon align="block-end">
            <slot name="field-end" :field="field">
              <FormFieldAsyncMessages
                v-if="hasMessagesFeature"
                :form="form"
                :name="name"
                :field-errors="field.state.meta.errors"
                :messages="messages"
                :messages-async="messagesAsync"
                :messages-async-debounce-ms="messagesAsyncDebounceMs"
              />
              <FieldError
                v-else-if="isInvalid(field)"
                :errors="field.state.meta.errors"
              />
              <span
                v-else-if="description"
                class="text-muted-foreground text-xs"
              >
                {{ description }}
              </span>
            </slot>
          </InputGroupAddon>
        </InputGroup>

        <!--
          Default: label above, bordered InputGroup around the input
          only (text-shaped), or the control direct (`input-group="false"`),
          errors below.
        -->
        <div v-else class="flex flex-col gap-2" :class="$props.class">
          <div
            v-if="label || $slots['label'] || $slots['label-end']"
            class="flex items-center gap-2"
          >
            <slot name="label" :field="field">
              <Label
                v-if="label"
                :for="name"
                class="text-foreground"
                :class="{
                  'inline-flex items-center gap-1.5':
                    ai || hint || field.state.meta.isValidating
                }"
              >
                {{ label }}
                <AiFieldHint v-if="ai" :name="name" />
                <TooltipHint v-if="hint" :text="hint" />
                <IconLoading
                  v-if="field.state.meta.isValidating"
                  class="text-muted-foreground h-3.5 w-3.5 animate-spin"
                  title="Checking…"
                />
              </Label>
            </slot>
            <slot name="label-end" :field="field">
              <span
                v-if="!required && label"
                class="text-muted-foreground/60 ml-auto text-xs font-normal"
              >
                optional
              </span>
            </slot>
          </div>

          <InputGroup v-if="wrapInInputGroup">
            <slot
              :field="field"
              :invalid="isInvalid(field)"
              :ai-loading="aiLoading"
              :handle-change="makeHandleChange(field)"
            />
          </InputGroup>
          <slot
            v-else
            :field="field"
            :invalid="isInvalid(field)"
            :ai-loading="aiLoading"
            :handle-change="makeHandleChange(field)"
          />

          <slot name="field-end" :field="field">
            <FormFieldAsyncMessages
              v-if="hasMessagesFeature"
              :form="form"
              :name="name"
              :field-errors="field.state.meta.errors"
              :messages="messages"
              :messages-async="messagesAsync"
              :messages-async-debounce-ms="messagesAsyncDebounceMs"
            />
            <FieldError
              v-else-if="isInvalid(field)"
              class="text-xs"
              :errors="field.state.meta.errors"
            />
            <span v-else-if="description" class="text-muted-foreground text-xs">
              {{ description }}
            </span>
          </slot>
        </div>
      </slot>
    </template>
  </component>
</template>
